import React from "react";
import {
  Stack,
  Box,
  TextField,
  Button,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MapComponent from "../ui/MapComponent";

// Glassmorphic container for the entire step
const GlassLocationContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.8
  )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.3
    )}, transparent)`,
    zIndex: 1,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${alpha(
      theme.palette.primary.main,
      0.03
    )} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(
      theme.palette.secondary.main,
      0.03
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },
  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Glassmorphic location button with floating animation
const GlassLocationButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.15
  )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  borderRadius: 16,
  color: theme.palette.primary.main,
  fontWeight: 600,
  textTransform: "none",
  padding: theme.spacing(1.5, 3),
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,

  // Floating animation
  animation: "float 3s ease-in-out infinite",
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px) translateZ(0)",
    },
    "50%": {
      transform: "translateY(-3px) translateZ(0)",
    },
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.2
    )}, transparent)`,
    transition: "left 0.6s ease",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "0",
    height: "0",
    background: `radial-gradient(circle, ${alpha(
      theme.palette.primary.main,
      0.3
    )} 0%, transparent 70%)`,
    borderRadius: "50%",
    transform: "translate(-50%, -50%)",
    transition: "all 0.3s ease",
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.25
    )} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    transform: "translateY(-2px)",
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
    animation: "none", // Stop floating on hover

    "&::before": {
      left: "100%",
    },

    "&::after": {
      width: "80px",
      height: "80px",
    },
  },

  "&:disabled": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.action.disabled,
      0.1
    )} 0%, ${alpha(theme.palette.action.disabled, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.action.disabled, 0.15)}`,
    color: theme.palette.action.disabled,
    transform: "none",
    animation: "none",
    boxShadow: "none",
  },

  "& .MuiCircularProgress-root": {
    color: theme.palette.primary.main,
    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

// Glassmorphic text field with enhanced styling
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.background.paper,
      0.6
    )} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    borderRadius: 12,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent, ${alpha(
        theme.palette.primary.main,
        0.2
      )}, transparent)`,
      opacity: 0,
      transition: "opacity 0.3s ease",
    },

    "&:hover": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.7
      )} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
      transform: "translateY(-1px)",
      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,

      "&::before": {
        opacity: 1,
      },
    },

    "&.Mui-focused": {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.background.paper,
        0.8
      )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
      transform: "translateY(-2px)",

      "&::before": {
        opacity: 1,
      },
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& input": {
      color: theme.palette.text.primary,
      fontWeight: 500,
      "&::placeholder": {
        color: alpha(theme.palette.text.primary, 0.6),
      },
    },
  },

  "& .MuiInputLabel-root": {
    color: alpha(theme.palette.text.primary, 0.8),
    fontWeight: 500,
    background: "transparent",
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

// Glassmorphic coordinate fields container
const GlassCoordinateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: 16,
  padding: theme.spacing(2),
  position: "relative",
  transition: "all 0.3s ease",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.3
    )}, transparent)`,
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.08
    )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

// Glassmorphic map container wrapper
const GlassMapWrapper = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
  backdropFilter: "blur(25px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 20,
  overflow: "hidden",
  position: "relative",
  boxShadow: `0 15px 45px ${alpha(theme.palette.common.black, 0.1)}`,
  height: 400, // Set a specific height for the map
  transition: "all 0.3s ease",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.4
    )}, transparent)`,
    zIndex: 1,
  },

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const LocationStep = ({
  isLoaded,
  formState,
  handleInputChange,
  handleMarkerDragEnd,
  onGetLocation,
  isLocating,
}) => {
  const theme = useTheme();

  return (
    <GlassLocationContainer>
      <Stack spacing={3}>
        {/* Location Button */}
        <Box display="flex" justifyContent="flex-end">
          <GlassLocationButton
            onClick={onGetLocation}
            disabled={!isLoaded || isLocating}
            startIcon={
              isLocating ? <CircularProgress size={20} /> : <MyLocationIcon />
            }
          >
            {isLocating ? "Getting Location..." : "Get Current Location"}
          </GlassLocationButton>
        </Box>

        {/* Map Component */}
        <GlassMapWrapper>
          <MapComponent
            isLoaded={isLoaded}
            mapCenter={formState.mapCenter}
            markerPosition={formState.markerPosition}
            onMarkerDragEnd={handleMarkerDragEnd}
          />
        </GlassMapWrapper>

        {/* Coordinates Display */}
        <GlassCoordinateContainer>
          <GlassTextField
            label="Latitude"
            value={
              formState.markerPosition.lat
                ? formState.markerPosition.lat.toFixed(4)
                : ""
            }
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
          />
          <GlassTextField
            label="Longitude"
            value={
              formState.markerPosition.lng
                ? formState.markerPosition.lng.toFixed(4)
                : ""
            }
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
          />
        </GlassCoordinateContainer>

        {/* Location Details */}
        <Stack spacing={2}>
          <GlassTextField
            label="City"
            name="city"
            value={formState.city}
            onChange={handleInputChange}
            fullWidth
          />
          <GlassTextField
            label="State/Province"
            name="province"
            value={formState.province}
            onChange={handleInputChange}
            fullWidth
          />
          <GlassTextField
            label="Country"
            name="country"
            value={formState.country}
            onChange={handleInputChange}
            fullWidth
          />
        </Stack>
      </Stack>
    </GlassLocationContainer>
  );
};

export default LocationStep;
